import type { BetterFetch } from "@better-fetch/fetch";
import type { Store } from "@di-devs/better-feature/types";
import { atom } from "nanostores";
import { richmenuContracts } from "../../shared/contracts/richmenu.contracts";
import type {
	CreateRichMenuTabGroupRequest,
	CreateRichMenuTabGroupResponse,
	GetRichMenuTabGroupResponse,
	GetRichMenuTabGroupsResponse,
	PublishRichmenuTabGroupResponse,
	RichMenuTabGroup,
	RichMenuTabGroupWithRichmenus,
	UnpublishRichmenuTabGroupResponse,
	UpdateRichmenuTabGroupRequest,
	UpdateRichmenuTabGroupResponse,
} from "../../shared/types/richmenu.types";
import {
	createQuerySetMap,
	extractListeners,
	extractQueries,
	extractSignals,
} from "../shared/queryFactory";

// Convert contract to endpoint configs
const richmenuTabGroupEndpointConfigs = {
	createRichMenuTabGroup: {
		path: richmenuContracts.createRichMenuTabGroup.path,
		method: richmenuContracts.createRichMenuTabGroup.contract.method,
	},
	getRichMenuTabGroups: {
		path: richmenuContracts.getRichMenuTabGroups.path,
		method: richmenuContracts.getRichMenuTabGroups.contract.method,
	},
	getRichMenuTabGroup: {
		path: richmenuContracts.getRichMenuTabGroup.path,
		method: richmenuContracts.getRichMenuTabGroup.contract.method,
	},
	publishRichmenuTabGroup: {
		path: richmenuContracts.publishRichmenuTabGroup.path,
		method: richmenuContracts.publishRichmenuTabGroup.contract.method,
	},
	unpublishRichmenuTabGroup: {
		path: richmenuContracts.unpublishRichmenuTabGroup.path,
		method: richmenuContracts.unpublishRichmenuTabGroup.contract.method,
	},
	updateRichmenuTabGroup: {
		path: richmenuContracts.updateRichmenuTabGroup.path,
		method: richmenuContracts.updateRichmenuTabGroup.contract.method,
	},
	deleteRichMenuTabGroup: {
		path: richmenuContracts.deleteRichMenuTabGroup.path,
		method: richmenuContracts.deleteRichMenuTabGroup.contract.method,
	},
} as const;

export function createRichmenuTabGroupPlugin(
	$fetch: BetterFetch,
	_$store?: Store,
) {
	const querySets = createQuerySetMap(richmenuTabGroupEndpointConfigs, $fetch);

	// Define atoms for richmenu tab group state
	const $richMenuTabGroups = atom<RichMenuTabGroup[]>([]);
	const $selectedRichMenuTabGroup = atom<RichMenuTabGroupWithRichmenus | null>(
		null,
	);
	const $isLoading = atom<boolean>(false);
	const $error = atom<string | null>(null);

	return {
		signals: extractSignals(querySets),
		queries: extractQueries(querySets),
		listeners: extractListeners(querySets),
		atoms: {
			$richMenuTabGroups,
			$selectedRichMenuTabGroup,
			$isLoading,
			$error,
		},
		actions: {
			async createRichMenuTabGroup(data: CreateRichMenuTabGroupRequest) {
				$isLoading.set(true);
				$error.set(null);

				try {
					const result = await $fetch<CreateRichMenuTabGroupResponse>(
						richmenuContracts.createRichMenuTabGroup.path,
						{
							method: richmenuContracts.createRichMenuTabGroup.contract.method,
							body: data,
						},
					);

					// Refresh the list after creation
					// await this.getRichMenuTabGroups();

					return result;
				} catch (error) {
					const errorMessage =
						error instanceof Error
							? error.message
							: "Failed to create rich menu tab group";
					$error.set(errorMessage);
					throw error;
				} finally {
					$isLoading.set(false);
				}
			},

			async getRichMenuTabGroups() {
				$isLoading.set(true);
				$error.set(null);

				try {
					const result = await $fetch<GetRichMenuTabGroupsResponse>(
						richmenuContracts.getRichMenuTabGroups.path,
						{
							method: richmenuContracts.getRichMenuTabGroups.contract.method,
						},
					);

					// if (result?.data) {
					// 	$richMenuTabGroups.set(result.data || []);
					// }

					return result;
				} catch (error) {
					const errorMessage =
						error instanceof Error
							? error.message
							: "Failed to get rich menu tab groups";
					$error.set(errorMessage);
					throw error;
				} finally {
					$isLoading.set(false);
				}
			},

			async getRichMenuTabGroup(tabGroupId: number) {
				$isLoading.set(true);
				$error.set(null);

				try {
					const result = await $fetch<GetRichMenuTabGroupResponse>(
						richmenuContracts.getRichMenuTabGroup.path.replace(
							":tabGroupId",
							String(tabGroupId),
						),
						{
							method: richmenuContracts.getRichMenuTabGroup.contract.method,
						},
					);

					// if (result?.data) {
					// 	$selectedRichMenuTabGroup.set(result.data);
					// }

					return result;
				} catch (error) {
					const errorMessage =
						error instanceof Error
							? error.message
							: "Failed to get rich menu tab group";
					$error.set(errorMessage);
					throw error;
				} finally {
					$isLoading.set(false);
				}
			},

			async publishRichmenuTabGroup(tabGroupId: number) {
				$isLoading.set(true);
				$error.set(null);

				try {
					const result = await $fetch<PublishRichmenuTabGroupResponse>(
						richmenuContracts.publishRichmenuTabGroup.path.replace(
							":tabGroupId",
							String(tabGroupId),
						),
						{
							method: richmenuContracts.publishRichmenuTabGroup.contract.method,
							body: {},
						},
					);

					// Refresh the list and selected item after publish
					// await this.getRichMenuTabGroups();
					// await this.getRichMenuTabGroup(tabGroupId);

					return result;
				} catch (error) {
					const errorMessage =
						error instanceof Error
							? error.message
							: "Failed to publish rich menu tab group";
					$error.set(errorMessage);
					throw error;
				} finally {
					$isLoading.set(false);
				}
			},

			async unpublishRichmenuTabGroup(tabGroupId: number) {
				$isLoading.set(true);
				$error.set(null);

				try {
					const result = await $fetch<UnpublishRichmenuTabGroupResponse>(
						richmenuContracts.unpublishRichmenuTabGroup.path.replace(
							":tabGroupId",
							String(tabGroupId),
						),
						{
							method:
								richmenuContracts.unpublishRichmenuTabGroup.contract.method,
							body: {},
						},
					);

					// Refresh the list and selected item after unpublish
					// await this.getRichMenuTabGroups();
					// await this.getRichMenuTabGroup(tabGroupId);

					return result;
				} catch (error) {
					const errorMessage =
						error instanceof Error
							? error.message
							: "Failed to unpublish rich menu tab group";
					$error.set(errorMessage);
					throw error;
				} finally {
					$isLoading.set(false);
				}
			},

			async updateRichmenuTabGroup(
				tabGroupId: number,
				data: UpdateRichmenuTabGroupRequest,
			) {
				$isLoading.set(true);
				$error.set(null);

				try {
					const result = await $fetch<UpdateRichmenuTabGroupResponse>(
						richmenuContracts.updateRichmenuTabGroup.path.replace(
							":tabGroupId",
							String(tabGroupId),
						),
						{
							method: richmenuContracts.updateRichmenuTabGroup.contract.method,
							body: data,
						},
					);

					// Refresh the list and selected item after update
					// await this.getRichMenuTabGroups();
					// await this.getRichMenuTabGroup(tabGroupId);

					return result;
				} catch (error) {
					const errorMessage =
						error instanceof Error
							? error.message
							: "Failed to update rich menu tab group";
					$error.set(errorMessage);
					throw error;
				} finally {
					$isLoading.set(false);
				}
			},

			async deleteRichMenuTabGroup(tabGroupId: number) {
				$isLoading.set(true);
				$error.set(null);

				try {
					const result = await $fetch(
						richmenuContracts.deleteRichMenuTabGroup.path.replace(
							":tabGroupId",
							String(tabGroupId),
						),
						{
							method: richmenuContracts.deleteRichMenuTabGroup.contract.method,
							body: {},
						},
					);

					// Clear selected item if it's the one being deleted
					const currentSelected = $selectedRichMenuTabGroup.get();
					if (currentSelected && currentSelected.groupId === tabGroupId) {
						$selectedRichMenuTabGroup.set(null);
					}

					// Remove the deleted item from the list
					const currentTabGroups = $richMenuTabGroups.get();
					const updatedTabGroups = currentTabGroups.filter(
						(group) => group.groupId !== tabGroupId,
					);
					$richMenuTabGroups.set(updatedTabGroups);

					return result;
				} catch (error) {
					const errorMessage =
						error instanceof Error
							? error.message
							: "Failed to delete rich menu tab group";
					$error.set(errorMessage);
					throw error;
				} finally {
					$isLoading.set(false);
				}
			},

			setLoading(isLoading: boolean) {
				$isLoading.set(isLoading);
			},

			setError(error: string | null) {
				$error.set(error);
			},

			selectRichMenuTabGroup(tabGroup: RichMenuTabGroupWithRichmenus | null) {
				$selectedRichMenuTabGroup.set(tabGroup);
			},
		},
		querySets,
	};
}
